import { F, INTL_ENTITY } from '@/utils/intl';
import React, { useEffect, useState } from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import { Button, Divider, Space } from 'antd';
import { ProForm, ProFormItem, ProFormText } from '@ant-design/pro-form';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import CopyableId from '@/components/CopyableId';
import {
  createProductGroupRequest,
  deleteProductGroupThumbnailRequest,
  editProductGroupRequest,
} from '@/services/api/productGroup';
import styles from './index.module.less';
import { processAPIResponseNow } from '@/utils/request';
import { URLParamBuilder } from '@/utils/functions';
import Paths from '@/services/paths';
import { API_PATH_PREFIX } from '@/constants/path';
import ThumbnailUpload from '@/components/ThumbnailUpload';
import { getRootUrl } from '@/constants/environment';
import * as ProductGroupSelectors from '@/selectors/productGroupSelectors';

const ProductGroupDrawer: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const drawer = useSelector(ProductGroupSelectors.selectProductGroupDrawer);
  const visible = !isEmpty(omit(drawer, ['toggledAt']));
  const dispatch = useDispatch();
  const [form] = ProForm.useForm();
  const intl = useIntl();

  const currentProductGroup = drawer?.productGroup;
  const editMode = !isEmpty(currentProductGroup);

  useEffect(() => {
    if (visible) {
      if (editMode) {
        form.setFieldsValue({
          label: currentProductGroup.label,
          migration_source: currentProductGroup.migration_source,
          migration_id: currentProductGroup.migration_id,
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, editMode, currentProductGroup, drawer?.toggledAt]);

  function toggle() {
    dispatch({
      type: 'productGroups/toggleProductGroupDrawer',
    });
  }

  const fetchProductGroups = () => {
    dispatch({
      type: 'productGroups/fetchProductGroups',
    });
  };

  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      const response =  await createProductGroupRequest({
        data: values,
      });

      processAPIResponseNow(response, {
        onSuccess: () => {
          fetchProductGroups();
          toggle();
        },
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleBlur = async (fieldName: string, value: any) => {
    if (editMode && currentProductGroup) {
      try {
        await form.validateFields([fieldName]);
        if (value !== currentProductGroup[fieldName]) {
          setLoading(true);
          const response = await editProductGroupRequest({
            params: { productGroupId: currentProductGroup.uuid },
            data: { [fieldName]: value },
          });
          processAPIResponseNow(response, {
            showSuccessMessage: true,
            successEntity: INTL_ENTITY.PRODUCT_GROUP,
            onSuccess: () => {
              fetchProductGroups();
            },
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const submitOnEnter = useSubmitOnEnter(
    editMode ? undefined : () => form.submit(),
    { enabled: !loading },
  );

  return (
    <Drawer
      title={
        editMode ? (
          <F
            id="common.entity.edit"
            values={{ entity: <F id="entity.product_group.singular" /> }}
          />
        ) : (
          <F
            id="common.entity.create"
            values={{ entity: <F id="entity.product_group.singular" /> }}
          />
        )
      }
      width={450}
      onClose={toggle}
      onOpenChange={toggle}
      open={visible}
      className={styles.productGroupDrawerRoot}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={toggle} disabled={loading}>
              <F
                id={
                  editMode ? 'common.actions.close' : 'common.actions.cancel'
                }
              />
            </Button>
            {!editMode && (
              <Button
                type="primary"
                onClick={() => form.submit()}
                loading={loading}
              >
                <F id="common.actions.save" values={{ entity: '' }} />
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <ProForm
        {...submitOnEnter}
        form={form}
        onFinish={handleFinish}
        submitter={false}
        layout="vertical"
      >
        <ProFormText
          name="label"
          label={intl.formatMessage({
            id: 'common.attribute.label',
            defaultMessage: 'Label',
          })}
          rules={[{ required: true }]}
          fieldProps={{
            onBlur: (e) => handleBlur('label', e.target.value),
          }}
        />

        <ProFormText
          name="migration_source"
          label={intl.formatMessage({
            id: 'common.attribute.migration_source',
            defaultMessage: 'Migration Source',
          })}
          fieldProps={{
            onBlur: (e) => handleBlur('migration_source', e.target.value),
          }}
        />

        <ProFormText
          name="migration_id"
          label={intl.formatMessage({
            id: 'common.attribute.migration_id',
            defaultMessage: 'Migration ID',
          })}
          fieldProps={{
            onBlur: (e) => handleBlur('migration_id', e.target.value),
          }}
        />

        {editMode && currentProductGroup && (
          <ProFormItem
            className={styles.productGroupThumbnailUpload}
            label={<F id="common.thumbnail" defaultMessage="Thumbnail" />}
          >
            <ThumbnailUpload
              name={'image'}
              deleteButtonClassName={styles.deleteButton}
              currentThumbnail={currentProductGroup.image_url}
              uploadUrl={URLParamBuilder(
                Paths.UPLOAD_THUMBNAIL_FOR_PRODUCT_GROUP,
                {
                  params: { productGroupId: currentProductGroup.uuid },
                  root: `${getRootUrl()}${API_PATH_PREFIX}`,
                },
              )}
              onDelete={() =>
                deleteProductGroupThumbnailRequest({
                  params: { productGroupId: currentProductGroup.uuid },
                })
              }
              onUploadSuccess={fetchProductGroups}
              onDeleteSuccess={fetchProductGroups}
            />
          </ProFormItem>
        )}
        <Divider style={{margin: "10px 0"}}/>
        {editMode && currentProductGroup && (
          <CopyableId id={currentProductGroup.uuid} />
        )}
      </ProForm>
    </Drawer>
  );
};

export default ProductGroupDrawer;
